// FILE: src/pages/AdminEditUserPage.jsx
// Edit a student's details (plan WP-8, matrix 0.6 / 9.x). Three parts:
// personal details, the licence (G1/G2 with a number, or "No licence yet":
// owner decision D11; same check as the server) and the account status,
// read-only, as icon + words. Only changed fields are sent, so an old
// record with a class the course doesn't accept can still have its name
// or phone corrected. The page uses the student from the list already
// loaded, and fetches the account by id (GET /api/admin/users/:id, WP-7,
// B-21) when it was opened directly (a link, a reload, the student's page).
// WP-6: staff accounts have a Digital Driving Instructor registration
// (needed to answer course-content support requests, BDE Policy Manual
// 3.4.2; PATCH { staffRegistration }, "" clears it) instead of a licence;
// students show their onboarding (tutorial, Code of Conduct and syllabus
// acknowledgements, read only); an invitation still waiting can be sent
// again (POST /api/admin/users/:id/resend-invite).
// RBAC: a student needs students.edit, a staff account staff.manage (the page
// explains when the role doesn't include it). For staff the role is chosen
// here (PATCH { staffRoleId }); nobody changes their own role. The server's
// refusals (LAST_ROLE_MANAGER, CANNOT_CHANGE_OWN_ROLE, PERMISSION_DENIED)
// are shown in words.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { updateUser } from "../features/adminUsersSlice";
import fetchWithAuth from "../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { UserStatusChips } from "../components/admin/AdminBits";
import { formatDate, formatDateTime, personName } from "../components/admin/format";
import {
  FIELD_FOR_CODE,
  LICENCE_CLASSES,
  LICENCE_HINT,
  licenceClassError,
  licenceNumberError,
  needsLicenceNumber,
  normaliseLicence
} from "../components/admin/studentForm";
import { errorCode, errorMessage, messageFor } from "../i18n/errors";
import { licenceClassLabel, roleLabel } from "../i18n/labels";
import { lockReasonText } from "../components/admin/studentStatus";
import { NoAccessState } from "../components/admin/NoAccess";
import StaffRolePicker from "../components/admin/StaffRolePicker";
import usePermissions from "../hooks/usePermissions";
import { STAFF_REGISTRATION_MAX, useRoles } from "../services/roles";

const STUDENT_FIELDS = ["firstName", "middleName", "lastName", "phone", "licenseClass", "licenseNumber"];
const STAFF_FIELDS = ["firstName", "middleName", "lastName", "phone", "staffRegistration", "staffRoleId"];
const ORDER = ["firstName", "lastName", "licenseClass", "licenseNumber", "staffRoleId", "staffRegistration"];

const DOCUMENTS = [
  { type: "CODE_OF_CONDUCT", label: "Code of Conduct" },
  { type: "SYLLABUS", label: "Syllabus" }
];

/** "Version 2, acknowledged 7 Oct 2026" lines for one document type, newest first. */
function acknowledged(user, type) {
  const list = Array.isArray(user?.onboarding?.acknowledgements) ? user.onboarding.acknowledgements : [];
  const mine = list.filter((a) => a && a.type === type).sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  if (!mine.length) return "Not acknowledged yet";
  return (
    <Box component="span" sx={{ display: "block" }}>
      {mine.map((a, i) => (
        <Box component="span" key={`${a.version}-${a.at}-${i}`} sx={{ display: "block" }}>
          {a.version ? `Version ${a.version}` : "A version"}, acknowledged {formatDate(a.at)}
        </Box>
      ))}
    </Box>
  );
}

/** "Send the invitation again" for an account whose invitation is still waiting. */
function ResendInvite({ user, staff }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await fetchWithAuth(`/api/admin/users/${user._id}/resend-invite`, { method: "POST" });
      setDone(true);
    } catch (err) {
      setError(err || "INTERNAL_ERROR");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ mb: 1.5 }}>
        {staff
          ? `${user.firstName || "This staff member"} hasn't set up their account yet.`
          : `${user.firstName || "This student"} hasn't finished registering yet. Their one-year enrolment starts when they do.`}
      </Typography>
      {done && (
        <Alert severity="success" role="status" sx={{ mb: 1.5 }}>
          A new invitation was emailed to {user.email}. The earlier link no longer works.
        </Alert>
      )}
      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 1.5 }}>
          {errorMessage(error)}
        </Alert>
      )}
      <Button variant="outlined" startIcon={<SendOutlinedIcon />} onClick={resend} disabled={busy}>
        {busy ? "Sending the invitation…" : "Send the invitation again"}
      </Button>
    </Box>
  );
}

function formFrom(user) {
  return {
    firstName: user?.firstName || "",
    middleName: user?.middleName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    // Old records may hold a class the course doesn't accept: start empty.
    licenseClass: LICENCE_CLASSES.includes(user?.licenseClass) ? user.licenseClass : "",
    licenseNumber: user?.licenseNumber || "",
    staffRegistration: user?.staffRegistration || "",
    staffRoleId: user?.staffRole?.id || user?.staffRoleId || ""
  };
}

export default function AdminEditUserPage() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { can } = usePermissions();
  const me = useSelector((state) => state.auth.user?._id);
  const { list } = useSelector((state) => state.adminUsers);
  const listed = useMemo(() => (Array.isArray(list) ? list : []).find((u) => String(u._id) === id), [list, id]);
  // B-21: not in the list (opened from a link or after a reload) → fetch it.
  const [fetched, setFetched] = useState({ user: null, loading: true, error: null });
  useEffect(() => {
    if (listed) return undefined;
    let live = true;
    setFetched({ user: null, loading: true, error: null });
    fetchWithAuth(`/api/admin/users/${encodeURIComponent(id)}`)
      .then((u) => live && setFetched({ user: u, loading: false, error: null }))
      .catch((error) => live && setFetched({ user: null, loading: false, error: error || "INTERNAL_ERROR" }));
    return () => {
      live = false;
    };
  }, [id, listed]);
  const [savedUser, setSavedUser] = useState(null);
  const base = listed || fetched.user;
  const user = useMemo(
    () => (base && savedUser && String(savedUser._id) === String(base._id) ? { ...base, ...savedUser } : base),
    [base, savedUser]
  );
  const loading = !listed && fetched.loading;

  const [form, setForm] = useState(() => formFrom(user));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [saved, setSaved] = useState(false);
  const refs = useRef({});
  const alertRef = useRef(null);
  const register = (name) => (el) => {
    refs.current[name] = el;
  };

  useEffect(() => {
    if (user) setForm(formFrom(user));
  }, [user]);
  // The roles are needed only to change another staff member's role.
  const roles = useRoles({ enabled: user?.role === "admin" && !!user?._id && String(user._id) !== String(me) });

  if (!user) {
    if (loading) return <LoadingState variant="page" label="Loading the student…" />;
    const missing = !fetched.error || ["USER_NOT_FOUND", "NOT_FOUND"].includes(errorCode(fetched.error));
    return (
      <Box>
        <PageHeader title="Edit student" breadcrumbs={[{ label: "Students", to: "/admin/users" }, { label: "Edit student" }]} />
        {missing ? (
          <EmptyState
            icon={PersonSearchOutlinedIcon}
            title={messageFor("USER_NOT_FOUND")}
            description="The link may be old, or the account was removed. Find the student in the student list and choose Edit."
            action={
              <Button component={RouterLink} to="/admin/users" variant="contained">
                Back to students
              </Button>
            }
          />
        ) : (
          <ErrorState error={fetched.error} title="We couldn't load this account" />
        )}
      </Box>
    );
  }

  const staff = user.role === "admin";
  const name = personName(user, staff ? "Staff member" : "Student");
  const isSelf = me && String(me) === String(user._id);
  const listCrumb = staff ? { label: "Staff", to: "/admin/staff" } : { label: "Students", to: "/admin/users" };
  if (!can(staff ? "staff.manage" : "students.edit")) {
    return <NoAccessState title={staff ? "You can't change staff accounts" : "You can't change student details"} />;
  }
  const original = formFrom(user);
  const changes = {};
  (staff ? STAFF_FIELDS : STUDENT_FIELDS).forEach((f) => {
    if (f === "staffRoleId" && (isSelf || !form.staffRoleId)) return;
    const value = f === "licenseNumber" ? normaliseLicence(form[f]) : form[f].trim();
    const before = f === "licenseNumber" ? normaliseLicence(original[f]) : original[f].trim();
    if (value !== before) changes[f] = value;
  });
  const licenceTouched = "licenseClass" in changes || "licenseNumber" in changes;
  const legacyClass = user.licenseClass && !LICENCE_CLASSES.includes(user.licenseClass);

  const validate = () => {
    const found = {};
    const who = staff ? "staff member" : "student";
    if (!form.firstName.trim()) found.firstName = `Enter the ${who}'s first name.`;
    if (!form.lastName.trim()) found.lastName = `Enter the ${who}'s last name.`;
    if (staff && form.staffRegistration.trim().length > STAFF_REGISTRATION_MAX) {
      found.staffRegistration = `Use ${STAFF_REGISTRATION_MAX} characters or fewer.`;
    }
    if (staff && !isSelf && !form.staffRoleId && Array.isArray(roles.roles) && roles.roles.length) {
      found.staffRoleId = "Choose a role.";
    }
    if (licenceTouched) {
      const cls = licenceClassError(form.licenseClass);
      if (cls) found.licenseClass = cls;
      const num = licenceNumberError(form.licenseNumber, form.licenseClass);
      if (num) found.licenseNumber = num;
    }
    return found;
  };

  const handleChange = (e) => {
    const { name: field, value } = e.target;
    // "No licence yet" has no number: clear it (the field is hidden).
    const clearNumber = field === "licenseClass" && !needsLicenceNumber(value);
    setForm((prev) => ({ ...prev, [field]: value, ...(clearNumber ? { licenseNumber: "" } : {}) }));
    setSaved(false);
    if (errors[field] || (clearNumber && errors.licenseNumber)) {
      setErrors((prev) => ({ ...prev, [field]: undefined, ...(clearNumber ? { licenseNumber: undefined } : {}) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);
    setSaved(false);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      const first = ORDER.find((f) => found[f]);
      refs.current[first]?.focus();
      return;
    }
    if (!Object.keys(changes).length) {
      setSaved(true);
      return;
    }
    // The server checks the class and number together.
    const updates = { ...changes };
    if (licenceTouched) {
      updates.licenseClass = form.licenseClass;
      updates.licenseNumber = needsLicenceNumber(form.licenseClass) ? normaliseLicence(form.licenseNumber) : "";
    }
    setSubmitting(true);
    try {
      const updated = await dispatch(updateUser({ userId: id, updates })).unwrap();
      if (updated?._id) setSavedUser(updated);
      setSaved(true);
    } catch (err) {
      setServerError(err || "INTERNAL_ERROR");
      const field = FIELD_FOR_CODE[errorCode(err)];
      if (field && field !== "email") {
        setErrors((prev) => ({ ...prev, [field]: errorMessage(err) }));
        setTimeout(() => refs.current[field]?.focus(), 0);
      } else {
        setTimeout(() => alertRef.current?.focus(), 0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title={`Edit ${name}`}
        breadcrumbs={[listCrumb, { label: name, to: `/admin/users/${user._id}` }, { label: "Edit" }]}
        description={
          staff
            ? "Change the staff member's name, phone, role or DDI registration number. Every change is recorded in the audit log."
            : "Correct the student's name, phone or licence. Every change is recorded in the audit log."
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" }
        }}
      >
        <Box component="form" noValidate onSubmit={handleSubmit} aria-label={`Edit ${name}`}>
          <Stack spacing={2.5}>
            {serverError && (
              <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
                <AlertTitle>We couldn't save the changes</AlertTitle>
                {errorMessage(serverError)}
              </Alert>
            )}
            {saved && (
              <Alert severity="success" role="status">
                Changes saved.
              </Alert>
            )}

            <Section id="personal" title="Personal details">
              <Stack spacing={2.5}>
                <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                  <TextField
                    id="firstName"
                    name="firstName"
                    label="First name"
                    value={form.firstName}
                    onChange={handleChange}
                    inputRef={register("firstName")}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    autoComplete="off"
                    required
                    fullWidth
                  />
                  <TextField
                    id="middleName"
                    name="middleName"
                    label="Middle name"
                    value={form.middleName}
                    onChange={handleChange}
                    helperText="Optional"
                    autoComplete="off"
                    fullWidth
                  />
                </Box>
                <TextField
                  id="lastName"
                  name="lastName"
                  label="Last name"
                  value={form.lastName}
                  onChange={handleChange}
                  inputRef={register("lastName")}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  autoComplete="off"
                  required
                  fullWidth
                />
                <TextField
                  id="phone"
                  name="phone"
                  type="tel"
                  label="Phone"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="off"
                  fullWidth
                />
              </Stack>
            </Section>

            {staff ? (
              <Section
                id="staff-role"
                title="Role and registration"
                description="The role decides what this person may do in the admin area. The Digital Driving Instructor registration is needed to answer course-content support requests (BDE Policy Manual 3.4.2)."
              >
                <Stack spacing={2.5}>
                  {isSelf ? (
                    <Typography data-testid="own-role">
                      Your role: <strong>{user.staffRole?.name || "Administrator"}</strong>. You can't change your own role: ask another
                      administrator.
                    </Typography>
                  ) : (
                    <StaffRolePicker
                      value={form.staffRoleId}
                      onChange={(v) => handleChange({ target: { name: "staffRoleId", value: v } })}
                      roles={roles.roles}
                      loading={roles.loading}
                      error={roles.error}
                      onRetry={roles.reload}
                      fieldError={errors.staffRoleId}
                      inputRef={register("staffRoleId")}
                    />
                  )}
                  <TextField
                    id="staffRegistration"
                    name="staffRegistration"
                    label="DDI registration number (needed to answer course-content questions)"
                    value={form.staffRegistration}
                    onChange={handleChange}
                    inputRef={register("staffRegistration")}
                    error={!!errors.staffRegistration}
                    helperText={
                      errors.staffRegistration ||
                      `Up to ${STAFF_REGISTRATION_MAX} characters. Leave it empty to remove the registration.`
                    }
                    autoComplete="off"
                    slotProps={{ htmlInput: { maxLength: STAFF_REGISTRATION_MAX, spellCheck: false } }}
                    fullWidth
                  />
                </Stack>
              </Section>
            ) : (
              <Section
                id="licence"
                title="Licence"
                description="Choose G1 or G2 and enter the licence number, or choose No licence yet if the student doesn't have a licence."
              >
                {legacyClass && (
                  <Alert severity="warning" sx={{ mb: 2.5 }}>
                    This record's licence class isn't G1, G2 or No licence yet. Choose the one that fits the student.
                  </Alert>
                )}
                <Box
                  sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.4fr)" } }}
                >
                  <TextField
                    select
                    id="licenseClass"
                    name="licenseClass"
                    label="Licence class"
                    value={form.licenseClass}
                    onChange={handleChange}
                    inputRef={register("licenseClass")}
                    error={!!errors.licenseClass}
                    helperText={errors.licenseClass}
                    required
                    fullWidth
                  >
                    {LICENCE_CLASSES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {licenceClassLabel(c)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {needsLicenceNumber(form.licenseClass) && (
                    <TextField
                      id="licenseNumber"
                      name="licenseNumber"
                      label="Licence number"
                      value={form.licenseNumber}
                      onChange={handleChange}
                      onBlur={() => setForm((prev) => ({ ...prev, licenseNumber: normaliseLicence(prev.licenseNumber) }))}
                      inputRef={register("licenseNumber")}
                      error={!!errors.licenseNumber}
                      helperText={errors.licenseNumber || LICENCE_HINT}
                      autoComplete="off"
                      slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: "characters" } }}
                      required
                      fullWidth
                    />
                  )}
                </Box>
              </Section>
            )}

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button component={RouterLink} to={listCrumb.to} variant="outlined">
                {staff ? "Back to staff" : "Back to students"}
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={submitting}>
                {submitting ? "Saving…" : "Save changes"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={2.5}>
          <Section
            id="account"
            title="Account status"
            description={
              user.invited
                ? "Read only, apart from sending the invitation again."
                : "Read only. Changes here are made by student support."
            }
          >
            <Box sx={{ mb: 2.5 }}>
              <UserStatusChips user={user} />
            </Box>
            {user.invited && (staff || can("students.invite")) && <ResendInvite user={user} staff={staff} />}
            <DetailList
              columns={1}
              items={[
                { label: "Email", value: user.email },
                { label: "Account", value: roleLabel(user.role) },
                staff && { label: "Staff role", value: user.staffRole?.name || "No role chosen" },
                { label: "Registration number", value: user.registrationNumber },
                { label: "Added on", value: formatDate(user.registrationDate || user.createdAt) },
                { label: "Enrolment started", value: formatDate(user.enrolmentStartedAt) },
                { label: "Enrolment ends", value: formatDate(user.enrolmentExpiresAt) },
                { label: "Last active", value: formatDateTime(user.lastActive) },
                { label: "Authenticator app", value: user.twoFactorEnabled ? "On" : "Off" },
                user.accountLocked && { label: "Locked since", value: formatDateTime(user.lockedAt) },
                user.accountLocked && user.lockReason && { label: "Lock reason", value: lockReasonText(user.lockReason) },
                staff && {
                  label: "DDI registration number",
                  value: user.staffRegistration || "None: can't answer course-content questions"
                }
              ]}
            />
          </Section>

          {!staff && (
            <Section
              id="onboarding"
              title="Onboarding"
              description="Read only. The student completes these steps before their first lesson."
            >
              <DetailList
                columns={1}
                items={[
                  {
                    label: "Course tutorial",
                    value: user.onboarding?.tutorialCompletedAt
                      ? `Done ${formatDate(user.onboarding.tutorialCompletedAt)}`
                      : "Not done yet"
                  },
                  ...DOCUMENTS.map((d) => ({ label: d.label, value: acknowledged(user, d.type) }))
                ]}
              />
            </Section>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
